/**
 * RecordsCard — the medical-records release journey for one contact, on the
 * ContactRail "Next steps" tab (Phase E). Per request it derives ONE current
 * step from GET /contacts/:id/records-requests and renders a vertical
 * mini-timeline (reached steps show their timestamp, the current step is bold
 * with a status pill, unreached steps are gray) so recruiters answer "where
 * is this stuck?" at a glance.
 *
 * Refetches on [contactId, reloadToken, pingTick] — every backend state
 * change in this phase emits the broadcast ping, which ContactDetail debounces
 * into pingTick (bumping reloadToken would yank the CommsThread scroll).
 */
import { useCallback, useEffect, useState } from 'react';
import { FileSignature, Loader2, Plus } from 'lucide-react';
import api from '../../../services/api';
import { Contact, RecordsRequest } from '../../types';
import { formatPhone, notifyStatsChanged, relTime } from '../../lib';
import RequestRecordsModal from './RequestRecordsModal';
import FaxOfficesModal from './FaxOfficesModal';

const errMsg = (e: any): string =>
  e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Request failed';

interface Props {
  contact: Contact;
  reloadToken?: number;
  /** Debounced realtime ping from ContactDetail — refetch without scroll side effects. */
  pingTick?: number;
  /** Fired after the card sends an SMS so the parent reloads timeline + cadence. */
  onSent?: () => void;
}

type StepKey = 'request' | 'sign' | 'fax' | 'records';

interface StepState {
  key: StepKey;
  label: string;
  detail: string | null;
  /** done | current | pending | failed */
  state: 'done' | 'current' | 'pending' | 'failed';
}

/** Derive the 4-step timeline for one request. */
function deriveSteps(r: RecordsRequest): { steps: StepState[]; current: StepKey } {
  const signed = r.status === 'signed';
  const faxes = r.faxes || [];
  const total = faxes.length;
  const delivered = faxes.filter((f) => f.status === 'delivered').length;
  const failed = faxes.filter((f) => f.status === 'failed').length;
  const received = (r.received || []).length > 0;

  const requestStep: StepState = {
    key: 'request',
    label: 'Request created',
    detail: relTime(r.created_at),
    state: 'done',
  };

  let signStep: StepState;
  if (signed) {
    signStep = { key: 'sign', label: 'Signed', detail: `by ${r.signer_name || 'patient'} · ${relTime(r.signed_at!)}`, state: 'done' };
  } else if (r.status === 'expired') {
    signStep = { key: 'sign', label: 'Sign link expired', detail: relTime(r.expires_at), state: 'failed' };
  } else if (r.status === 'cancelled') {
    signStep = { key: 'sign', label: 'Cancelled', detail: null, state: 'failed' };
  } else {
    signStep = {
      key: 'sign',
      label: 'Awaiting signature',
      detail: `${r.sent_at ? `Sign link sent ${relTime(r.sent_at)}` : 'Link not texted yet'}${r.viewed_at ? ' · opened' : ''}`,
      state: 'current',
    };
  }

  const officeWord = (n: number) => `${n} office${n === 1 ? '' : 's'}`;
  // A busy/no-answer line that the system is auto-retrying on its own — surfaced
  // as "in progress", not a failure, so a recruiter doesn't resend by hand.
  const retrying = faxes.find((f) => f.auto_retry)?.auto_retry || null;
  let faxStep: StepState;
  if (!signed) {
    faxStep = { key: 'fax', label: 'Fax to office(s)', detail: null, state: 'pending' };
  } else if (total === 0) {
    faxStep = { key: 'fax', label: 'Add office fax number(s) to send', detail: 'Use "Add fax & send"', state: 'current' };
  } else if (retrying) {
    faxStep = {
      key: 'fax',
      label: `Faxing release to ${officeWord(total)}…`,
      detail: `Line was busy — auto-retrying (attempt ${retrying.attempt} of ${retrying.max})`,
      state: 'current',
    };
  } else if (failed > 0) {
    faxStep = { key: 'fax', label: 'Fax failed', detail: `${failed} of ${officeWord(total)} failed`, state: 'failed' };
  } else if (delivered === total) {
    faxStep = { key: 'fax', label: `Release delivered to ${officeWord(total)}`, detail: null, state: 'done' };
  } else {
    faxStep = { key: 'fax', label: `Faxing release to ${officeWord(total)}…`, detail: null, state: 'current' };
  }

  const recordsStep: StepState = received
    ? { key: 'records', label: 'Records received', detail: relTime(r.received[0].created_at), state: 'done' }
    : {
        key: 'records',
        label: 'Records back from office',
        detail: delivered > 0 ? 'Waiting on the office' : null,
        state: delivered > 0 ? 'current' : 'pending',
      };

  const steps = [requestStep, signStep, faxStep, recordsStep];
  const current =
    received ? 'records'
      : faxStep.state === 'current' || faxStep.state === 'failed' ? 'fax'
        : signStep.state !== 'done' ? 'sign'
          : 'records';
  return { steps, current };
}

function pillCls(state: StepState['state']): string {
  switch (state) {
    case 'done': return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300';
    case 'failed': return 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300';
    case 'current': return 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300';
    default: return 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400';
  }
}

export default function RecordsCard({ contact, reloadToken = 0, pingTick = 0, onSent }: Props) {
  const contactId = contact.id;
  const [requests, setRequests] = useState<RecordsRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [faxModalReq, setFaxModalReq] = useState<RecordsRequest | null>(null);

  const load = useCallback(async (initial = false) => {
    if (initial) setLoading(true);
    try {
      const res = await api.get(`/api/crm/contacts/${contactId}/records-requests`);
      setRequests(res.data?.requests || []);
      setError(null);
    } catch (e: any) {
      if (initial) setError(errMsg(e));
    } finally {
      if (initial) setLoading(false);
    }
  }, [contactId]);

  useEffect(() => { load(true); }, [load]);
  useEffect(() => { if (reloadToken || pingTick) load(false); }, [reloadToken, pingTick, load]);

  const viewDocument = async (documentId: string) => {
    setActionError(null);
    try {
      const res = await api.get(`/api/crm/documents/${documentId}/url`);
      if (res.data?.url) window.open(res.data.url, '_blank', 'noopener');
    } catch (e: any) {
      setActionError(errMsg(e));
    }
  };

  const act = async (id: string, fn: () => Promise<void>) => {
    if (busyId) return;
    setBusyId(id);
    setActionError(null);
    setNotice(null);
    try {
      await fn();
    } catch (e: any) {
      const d = e?.response?.data || {};
      const base = d.detail || d.error || e.message || 'Action failed';
      setActionError(d.hint ? `${base} — ${d.hint}` : base);
    } finally {
      setBusyId(null);
    }
  };

  const resend = (r: RecordsRequest) => act(r.id, async () => {
    await api.post(`/api/crm/signature-requests/${r.id}/resend`);
    setNotice('Sign link re-texted to the patient.');
    onSent?.();
    await load(false);
  });

  const copyLink = (r: RecordsRequest) => act(r.id, async () => {
    // send:false returns the live URL without texting the patient.
    const res = await api.post(`/api/crm/signature-requests/${r.id}/resend`, { send: false });
    const url = res.data?.url;
    let copied = false;
    if (url && navigator.clipboard) {
      try { await navigator.clipboard.writeText(url); copied = true; } catch { /* show inline */ }
    }
    setNotice(copied ? 'Sign link copied to clipboard.' : url ? `Link: ${url}` : 'Link unavailable.');
    await load(false);
  });

  const cancel = (r: RecordsRequest) => act(r.id, async () => {
    await api.post(`/api/crm/signature-requests/${r.id}/cancel`);
    setNotice('Request cancelled.');
    await load(false);
  });

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:p-6">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="inline-flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
          <FileSignature className="h-4 w-4 text-primary-600 dark:text-primary-400" />
          Medical records
        </h3>
        <button
          onClick={() => setShowModal(true)}
          title="Text the patient a records release to e-sign"
          className="inline-flex items-center gap-1 rounded-lg bg-primary-600 px-2.5 py-1.5 text-xs font-medium text-white transition-colors duration-150 hover:bg-primary-700 min-h-[44px] sm:min-h-0"
        >
          <Plus className="h-3.5 w-3.5" /> Request records
        </button>
      </div>

      {notice && (
        <div className="mb-3 rounded-lg border border-primary-200 bg-primary-50 p-2.5 text-xs text-primary-800 dark:border-primary-800 dark:bg-primary-900/20 dark:text-primary-300">
          {notice}
        </div>
      )}
      {(error || actionError) && (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 p-2.5 text-xs text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          {error || actionError}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-4">
          <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
        </div>
      ) : requests.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400">
          No records requests yet — use Request records to text the patient a release to sign.
        </p>
      ) : (
        <div className="space-y-4">
          {requests.map((r) => {
            const { steps } = deriveSteps(r);
            const live = ['created', 'sent', 'viewed'].includes(r.status);
            const busy = busyId === r.id;
            const reqFaxes = r.faxes || [];
            const anyFaxFailed = reqFaxes.some((f) => f.status === 'failed');
            const noFax = reqFaxes.length === 0;
            return (
              <div key={r.id} className="rounded-lg border border-gray-100 p-3 dark:border-gray-700/60">
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">Records release</span>
                  {r.lookback_label && <span className="shrink-0 text-xs text-gray-400">{r.lookback_label}</span>}
                </div>
                {r.offices && r.offices.length > 0 && (
                  <div className="mt-1 space-y-0.5">
                    {r.offices.map((o, i) => (
                      <p key={i} className="truncate text-xs text-gray-500 dark:text-gray-400">
                        {o.name || 'Office'}{o.fax ? ` · ${formatPhone(o.fax)}` : ''}
                      </p>
                    ))}
                  </div>
                )}

                <ol className="mt-2 space-y-1.5">
                  {steps.map((s) => (
                    <li key={s.key} className="flex items-start gap-2">
                      <span
                        aria-hidden="true"
                        className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${
                          s.state === 'done' ? 'bg-green-500'
                            : s.state === 'failed' ? 'bg-red-500'
                              : s.state === 'current' ? 'bg-cyan-500'
                                : 'bg-gray-300 dark:bg-gray-600'
                        }`}
                      />
                      <div className="min-w-0">
                        <span className={`text-xs ${
                          s.state === 'current' || s.state === 'failed'
                            ? 'font-semibold text-gray-900 dark:text-gray-100'
                            : s.state === 'done' ? 'text-gray-700 dark:text-gray-300' : 'text-gray-400 dark:text-gray-500'
                        }`}>
                          {s.label}
                          {(s.state === 'current' || s.state === 'failed') && (
                            <span className={`ml-1.5 inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium ${pillCls(s.state)}`}>
                              {s.state === 'failed' ? 'attention' : 'in progress'}
                            </span>
                          )}
                        </span>
                        {s.detail && <p className="text-[11px] text-gray-400">{s.detail}</p>}
                      </div>
                    </li>
                  ))}
                </ol>

                <div className="mt-2.5 flex flex-wrap gap-x-3 gap-y-1.5">
                  {busy && <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400" />}
                  {live && !busy && (
                    <>
                      <button onClick={() => resend(r)} className="min-h-[44px] text-xs font-medium text-primary-700 hover:underline dark:text-primary-400 sm:min-h-0">Resend</button>
                      <button onClick={() => copyLink(r)} className="min-h-[44px] text-xs font-medium text-primary-700 hover:underline dark:text-primary-400 sm:min-h-0">Copy link</button>
                      <button onClick={() => cancel(r)} className="min-h-[44px] text-xs font-medium text-gray-500 hover:underline dark:text-gray-400 sm:min-h-0">Cancel</button>
                    </>
                  )}
                  {r.status === 'expired' && !busy && (
                    <button onClick={() => setShowModal(true)} className="min-h-[44px] text-xs font-medium text-primary-700 hover:underline dark:text-primary-400 sm:min-h-0">New request</button>
                  )}
                  {r.status === 'signed' && !busy && (
                    <>
                      {r.signed_document_id && (
                        <button onClick={() => setFaxModalReq(r)} className="min-h-[44px] text-xs font-medium text-primary-700 hover:underline dark:text-primary-400 sm:min-h-0">
                          {anyFaxFailed ? 'Retry fax' : noFax ? 'Add fax & send' : 'Fax another office'}
                        </button>
                      )}
                      {r.signed_document_id && (
                        <button onClick={() => viewDocument(r.signed_document_id!)} className="min-h-[44px] text-xs font-medium text-gray-500 hover:underline dark:text-gray-400 sm:min-h-0">View PDF</button>
                      )}
                      {(r.received || []).map((doc) => (
                        <button key={doc.document_id} onClick={() => viewDocument(doc.document_id)} className="min-h-[44px] text-xs font-medium text-green-700 hover:underline dark:text-green-400 sm:min-h-0">
                          View records ({relTime(doc.created_at)})
                        </button>
                      ))}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showModal && (
        <RequestRecordsModal
          contact={contact}
          onClose={() => setShowModal(false)}
          onBlocked={() => load(false)}
          onCreated={(sent) => {
            setShowModal(false);
            setNotice(sent ? 'Sign link texted to the patient.' : 'Request created.');
            if (sent) {
              notifyStatsChanged();
              onSent?.();
            }
            load(false);
          }}
        />
      )}

      {faxModalReq && (
        <FaxOfficesModal
          request={faxModalReq}
          onClose={() => setFaxModalReq(null)}
          onSent={(n) => {
            setFaxModalReq(null);
            setNotice(`Fax queued to ${n} office${n === 1 ? '' : 's'}.`);
            notifyStatsChanged();
            load(false);
          }}
        />
      )}
    </div>
  );
}
